import React, { useState } from 'react';
import { ArrowLeft, Database, Loader2, Mail, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import { reportsApi } from '../api';

type ModuleProgress = {
  module_name?: string;
  module_progress?: string;
  module_score?: string;
};

type BatchConsumptionRecord = {
  firstname?: string;
  lastname?: string;
  email?: string;
  modules?: ModuleProgress[];
};

const BatchConsumption: React.FC = () => {
  const [batchCode, setBatchCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [records, setRecords] = useState<BatchConsumptionRecord[] | null>(null);
  const [raw, setRaw] = useState<any>(null);
  const [status, setStatus] = useState<string | number | null>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = batchCode.trim();
    if (!trimmed) {
      setError('Please enter a batch code.');
      return;
    }
    setLoading(true);
    setError(null);
    setRecords(null);
    setRaw(null);
    setStatus(null);

    try {
      const response = await reportsApi.getBatchConsumption({ batchCode: trimmed });
      setRecords(
        Array.isArray(response?.records)
          ? response.records
          : Array.isArray(response?.data)
            ? response.data
            : null
      );
      setStatus(response?.status ?? null);
      setRaw(response ?? null);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Unable to fetch batch consumption.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Link
            to="/dashboard"
            className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Dashboard
          </Link>
          <h1 className="mt-3 text-2xl font-bold text-gray-900">Batch Consumption</h1>
          <p className="mt-1 text-sm text-gray-700 max-w-2xl">
            Call the Knowlens batch consumption endpoint through the backend proxy. UI is a placeholder until the response contract is finalised.
          </p>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg border border-gray-100 p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-primary-50 p-3 text-primary-600">
            <Database className="h-5 w-5" />
          </div>
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-gray-900">Query a batch</h2>
            <p className="text-sm text-gray-600">
              Uses the fixed client id from config and hashes the request in the backend with the Knowlens X-Signature scheme. Enter a batch code to trigger the call.
            </p>
          </div>
        </div>

        <form className="grid grid-cols-1 md:grid-cols-3 gap-4" onSubmit={handleSubmit}>
          <div className="md:col-span-2">
            <label htmlFor="batch-code" className="form-label">
              Batch code
            </label>
            <input
              id="batch-code"
              name="batch-code"
              type="text"
              value={batchCode}
              onChange={(event) => setBatchCode(event.target.value)}
              placeholder="B001"
              className="form-input"
            />
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              className="btn-primary w-full justify-center"
              disabled={loading}
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Fetch
            </button>
          </div>
        </form>

        {error && (
          <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-md">
              <p className="text-xs text-gray-500">API Status</p>
              <p className="text-sm font-semibold text-gray-900">{status ?? '—'}</p>
            </div>
            {records && (
              <div className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-md">
                <p className="text-xs text-gray-500">Trainees</p>
                <p className="text-sm font-semibold text-gray-900">{records.length}</p>
              </div>
            )}
          </div>

          <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <p className="text-sm font-medium text-gray-900">
                Trainee consumption {records ? `(${records.length})` : ''}
              </p>
              {loading && <span className="text-xs text-gray-500">Loading…</span>}
            </div>
            {records && records.length > 0 ? (
              <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3">
                {records.map((row, idx) => (
                  <div
                    key={`${row.email || ''}-${idx}`}
                    className="rounded-lg border border-gray-200 p-4 shadow-sm hover:shadow transition-shadow duration-150"
                  >
                    <div className="flex items-start gap-3">
                      <div className="rounded-full bg-primary-50 p-2 text-primary-600">
                        <User className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">
                          {[row.firstname, row.lastname].filter(Boolean).join(' ') || '—'}
                        </p>
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <Mail className="h-3 w-3" /> {row.email || 'No email'}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 space-y-2">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Modules</p>
                      <div className="space-y-2">
                        {(row.modules ?? []).map((mod, modIdx) => (
                          <div key={modIdx} className="rounded-md bg-gray-50 border border-gray-200 px-3 py-2">
                            <div className="text-sm font-semibold text-gray-900">{mod.module_name || 'Unnamed module'}</div>
                            <div className="mt-1 flex items-center gap-2 text-xs text-gray-600">
                              <span className="inline-flex items-center gap-1 rounded-full bg-white border border-gray-200 px-2 py-0.5">
                                Progress: {mod.module_progress ?? '0'}
                              </span>
                              <span className="inline-flex items-center gap-1 rounded-full bg-white border border-gray-200 px-2 py-0.5">
                                Score: {mod.module_score ?? '0'}
                              </span>
                            </div>
                          </div>
                        ))}
                        {(!row.modules || row.modules.length === 0) && (
                          <div className="text-xs text-gray-500">No module data provided.</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                {loading ? 'Fetching batch consumption...' : 'No data yet. Submit a batch code to view records.'}
              </div>
            )}
          </div>

          {/* <div className="bg-gray-50 rounded-md border border-dashed border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-gray-800">Raw response</p>
              {loading && <span className="text-xs text-gray-500">Loading…</span>}
            </div>
            <div className="mt-3">
              {raw ? (
                <pre className="text-xs text-gray-800 bg-white border border-gray-200 rounded-md p-3 overflow-auto">
                  {JSON.stringify(raw, null, 2)}
                </pre>
              ) : (
                <div className="text-sm text-gray-500">
                  No response yet.
                </div>
              )}
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default BatchConsumption;
